import {
  Button,
  Card,
  CustomerRoleTypeBadge,
  HSpacer,
  IconButton,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { RetailerAvatar } from "@/components/DesignSystem/Avatar/RetailerAvatar";
import { ChurnBanner } from '@/components/shared/ChurnBanner';
import { ProgramTags } from '@/components/shared/ProgramTags';
import { SubwalletListDialog } from '@/components/shared/SubwalletBalance/SubwalletListDialog';
import { TestUserBadge } from '@/components/shared/TestUserBadge';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from "@/contexts/dataSync/AuthenticationContext";
import { useCanWrite } from '@/hooks/useCanWrite';
import { CustomerRoleBadge } from "@/pages/CustomerDashboard/CustomerRoleBadge";
import { AboutAccountRolesBanner } from "@/pages/CustomerDashboard/LinkedAccounts";
import { PointsExpirationBanner } from "@/pages/CustomerDashboard/PointsExpirationBanner";
import {
  ProgramPointsExpirationModal,
} from '@/pages/CustomerDashboard/ProgramPointsExpirationModal';
import { AssociatedSellersDialog } from '@/pages/FarmerList/AssociatedSellersDialog';
import { EditCustomerDialog } from '@/pages/FarmerList/EditCustomerDialog';
import { EnergyProgramStatus } from '@/pages/FarmerList/Energy/EnergyProgramStatus';
import { EnergyStatusCard } from '@/pages/FarmerList/Energy/EnergyStatusCard';
import { EnergyTierOverrideModal } from '@/pages/FarmerList/Energy/EnergyTierOverrideModal';
import { ManageBehaviorsModal } from '@/pages/FarmerList/Energy/ManageBehaviorsModal';
import { ProgramMembershipCard } from '@/pages/FarmerList/Energy/ProgramMembershipCard';
import { LinkedAccountsDialog } from '@/pages/FarmerList/LinkedAccountsDialog';
import { PointsActivityModal } from '@/pages/FarmerList/PointsActivity/PointsActivityModal';
import { RewardsProgramStatus } from '@/pages/FarmerList/RewardsProgramStatus';
import { TierOverrideModal } from '@/pages/FarmerList/TierOverrideModal';
import { TierStatusCard } from '@/pages/FarmerList/TierStatusCard';
import { UpdatePointsDialog } from '@/pages/FarmerList/UpdatePointsDialog';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { EnergyApi } from '@/utilities/api/EnergyApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { UserApi } from '@/utilities/api/UserApi';
import { StringUtility } from '@/utilities/String';
import { ApiUser } from "@api/interfaces";
import EditOutlined from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Box, CircularProgress, Container, Divider, Grid, Stack, Tooltip } from '@mui/material';
import { AccountGroupRole, ChurnStatus, CIBPageName, UserType } from '@shared/enums';
import {
  calculateAvailablePoints,
  calculateLockedPoints,
  calculateReservedPoints,
  formatNumber,
  formatPhoneNumber,
} from '@shared/utilities';
import { getAccountName } from '@shared/utilities/UserUtilities';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { JobApi } from "@/utilities/api/JobApi";
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';

interface CustomerDetailsProps {
  hideBackButton?: boolean,
  id?: string,
  onViewAccount?: (customer: ApiUser) => void,
}

interface CustomerDataCardProps {
  customer: ApiUser,
  label: string,
  labelStartIconSrc?: string,
  tooltipText?: string,
  value: string | null,
}

const CustomerDataCard = ({
  customer,
  label,
  labelStartIconSrc,
  tooltipText,
  value,
}: CustomerDataCardProps) => (
  <Card testID={`rewards-${label}-card`}>
    <Stack alignItems="center" direction="row">
      <Text category="overline">
        {label}
      </Text>
      <HSpacer size="2" />
      <HSpacer size="1" />
      {tooltipText && (
        <Tooltip placement="top" title={tooltipText}>
          <InfoOutlinedIcon sx={{
            height: "18px",
            width: "18px",
            cursor: "pointer",
          }} />
        </Tooltip>
      )}
    </Stack>
    <VSpacer size="2" />
    <Stack alignItems="center" direction="row" justifyContent="space-between">
      <Stack
        alignItems="center"
        direction="row"
        justifyContent={value === null ? 'center' : 'flex-start'}
        sx={{ width: '100%' }}
      >
        {!!labelStartIconSrc && value !== null && (
          <>
            <Box
              alt="Tier image"
              component="img"
              src={labelStartIconSrc}
              sx={{
                height: '23.704px',
                objectFit: 'contain',
                width: '30.088px',
              }}
            />
            <HSpacer size="2" />
          </>
        )}
        <Text category="body-xlarge" sx={{ fontWeight: value === null ? '800' : '500' }}>
          {value === null ? '――' : value}
        </Text>
      </Stack>
      {!!customer.accountGroupRole && (
        <CustomerRoleTypeBadge
          accountGroupRole={customer.accountGroupRole}
          testID="status-card-shared-badge"
        />
      )}
    </Stack>
  </Card>
);

const CustomerDetails = ({
  hideBackButton = false,
  id,
  onViewAccount,
}: CustomerDetailsProps) => {
  const { t } = useTranslation();
  const { id: paramId = '' } = useParams();
  const customerId = id ?? paramId;
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showUpdatePointsModal, setShowUpdatePointsModal] = useState(false);
  const [showPointsActivityModal, setShowPointsActivityModal] = useState(false);
  const [showAssociatedSellersModal, setShowAssociatedSellersModal] = useState(false);
  const [showLinkedAccountsModal, setShowLinkedAccountsModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showEnergyOverrideModal, setShowEnergyOverrideModal] = useState(false);
  const [behaviorsModalMode, setBehaviorsModalMode] = useState<'manage' | 'view' | null>(null);
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { isInternal, isRetailerAdmin, retailer: retailerUser } = useAuthentication();
  const canWrite = useCanWrite();

  useEffect(() => {
    setShowLinkedAccountsModal(false);
  }, [customerId]);

  const { data: customer, isFetched } = useQuery(
    [QueryKeys.GET_USER, customerId],
    () => UserApi.get(customerId), {
      enabled: !!customerId,
      onError: (error: { message: string }) => {
        openSnackbar(error.message || t($ => $.common.error.fetchingCustomer));
      },
    },
  );

  const { data: farmerRewards } = useQuery(
    [QueryKeys.GET_FARMER_REWARDS, customerId],
    () => UserApi.getFarmerRewards(customerId),
  );

  const { data: retailer } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, customer?.retailerId],
    () => {
      return RetailerApi.get(customer!.retailerId!);
    },
    { enabled: !!customer && !!customer.retailerId },
  );

  const { data: retailerIsProcessing } = useQuery(
    [QueryKeys.GET_JOB_IS_PROCESSING, customer?.retailerId],
    () => JobApi.getIsProcessing(customer!.retailerId!),
    {
      enabled: !!customer?.retailerId,
      refetchInterval: (result) => (result ? 5000 : false),
    },
  );

  const isJobProcessing = retailerIsProcessing ?? true;

  const isSecondaryAccount = customer?.accountGroupRole === AccountGroupRole.Secondary;

  const { data: rewardsProgram } = useQuery(
    [QueryKeys.GET_ACTIVE_PROGRAM_FOR_RETAILER, customer?.retailerId],
    () => RewardsProgramApi.getActiveProgramForRetailer(customer!.retailerId!),
    { enabled: !!customer && !!customer.retailerId },
  );

  const { data: linkedAccounts } = useQuery(
    [QueryKeys.GET_LINKED_ACCOUNTS, customerId],
    () => UserApi.getLinkedAccounts(customerId), {
      enabled: !!customer && customer.userType == UserType.Farmer,
    },
  );

  const { data: associatedSellers } = useQuery(
    [QueryKeys.GET_ASSOCIATED_SELLERS, customerId],
    () => UserApi.getAssociatedSellers(customerId), {
      enabled: !!customer && customer.userType == UserType.Farmer,
    },
  );

  // Loaded for every customer: its energy wallets are left out of agronomy's points, even for a
  // customer who has left energy.
  const { data: energyCustomer, isLoading: isEnergyCustomerLoading } = useQuery(
    [QueryKeys.GET_ENERGY_CUSTOMER, customerId],
    () => EnergyApi.getCustomer(customerId),
    { enabled: !!customer },
  );

  const { data: balances } = useQuery(
    [QueryKeys.LIST_SUBWALLET_BALANCES, customerId],
    async () => {
      return UserApi.getSubwalletBalances(customerId);
    },
    { enabled: customer?.accountGroupRole !== AccountGroupRole.Secondary },
  );

  if (
    !customerId
    || (isFetched && !customer)
    || (customer && customer.userType !== UserType.Farmer)
  ) {
    navigate(Routes.HOMEPAGE);
    return null;
  }

  if (!customer) {
    return <CircularProgress />;
  }

  const address = StringUtility.formatAddress(
    customer.address1,
    customer.address2,
    customer.city,
    customer.state,
    customer.postal,
  ) || '-';
  const phone = customer.telephone ? formatPhoneNumber(customer.telephone, 'paren') : '-';
  const showRewardsProgramStatus = !!retailer && !isSecondaryAccount;
  const primaryCustomerId = linkedAccounts?.find(
    (user) => user.accountGroupRole === AccountGroupRole.Primary,
  )?.id ?? null;

  const Header = () => {
    if (!retailer) {
      return null;
    }
    return (
      <Stack
        alignItems="center"
        bgcolor="#EBEBEB"
        direction="row"
        height="36px"
        justifyContent="center"
      >
        <RetailerAvatar retailer={retailer} size={24} />
        <HSpacer size="3" />
        <Text category="label-medium">
          {t($ => $.customers.details.retailerCustomerAccount, { retailerName: retailer.name })}
        </Text>
      </Stack>
    );
  };

  const farmerReward = farmerRewards?.find(
    (rewards) => rewards.programId === rewardsProgram?.id,
  );

  // Energy points from any program year live in that year's wallet, never in agronomy's totals.
  const energySubwalletIds = new Set(energyCustomer?.energySubwalletIds ?? []);
  const agronomyBalances = (balances ?? [])
    .filter(({ subwalletId }) => !energySubwalletIds.has(subwalletId));
  const agronomyPointsAvailable = calculateAvailablePoints(agronomyBalances);
  const agronomyPointsLocked = calculateLockedPoints(agronomyBalances);
  const totalPointsReserved = calculateReservedPoints(balances ?? []);

  const accountName = getAccountName(customer);
  const canAddOverride = isInternal && !!customer.externalId;
  const energy = energyCustomer?.energy ?? null;
  // Without energy data (e.g. for salespeople), show the agronomy blocks.
  const isAgronomyMember = customer.isAgronomyMember !== false || !energy;
  const isEnergyReadOnly = !isInternal;
  const energyPointsAvailable = energy?.pointsAvailable ?? 0;
  const showMembershipCard = !!energyCustomer && (isInternal || !!customer.isEnergyMember);
  // A former energy customer's energy wallets stay hidden.
  const walletBalances = customer.isEnergyMember ? balances : agronomyBalances;

  return (
    <Container maxWidth="lg">
      {isInternal && (
        <>
          <VSpacer size="4" />
          <Header />
        </>
      )}
      {!!customer.accountGroupRole && !!linkedAccounts?.length && (
        <AboutAccountRolesBanner
          customerRole={customer.accountGroupRole}
          linkedAccounts={linkedAccounts}
          pageName={CIBPageName.RP_CustomerRecord}
        />
      )}
      {!hideBackButton && (
        <Button
          onClick={() => navigate(-1)}
          startIcon={<KeyboardBackspaceIcon />}
          sx={{ display: 'flex', align: 'center' }}
          testID="back-to-customers-button"
          variant="text"
        >
          {t($ => $.customers.details.backToCustomers)}
        </Button>
      )}
      <PointsExpirationBanner
        customer={customer}
        onNavigateToWallet={
          () => setShowWalletModal(true)
        }
      />
      <VSpacer size="3"/>
      <Grid container spacing={3}>
        <Grid item m={0} md={4} sm={12}>
          <Card testID="farmer-details-card">
            <Stack alignItems="flex-start" direction="row" justifyContent="space-between">
              <Stack>
                {!!customer.businessName && customer.businessName !== accountName && (
                  <Text category="overline" textTransform="uppercase">
                    {customer.businessName}
                  </Text>
                )}
                <VSpacer size="1" />
                <Text breakWord category="title-large">
                  {accountName}
                </Text>
              </Stack>
              {isInternal && (
                <IconButton
                  aria-label={t($ => $.customers.details.editCustomer.editButton)}
                  disabled={!canWrite}
                  onClick={() => setShowEditCustomerModal(true)}
                  testID="edit-customer-button"
                >
                  <EditOutlined />
                </IconButton>
              )}
            </Stack>
            <VSpacer size="2" />
            <ProgramTags
              isAgronomyMember={customer.isAgronomyMember}
              isEnergyMember={customer.isEnergyMember}
              testID="customer-details-program-tags"
            />
            {customer.isTest && (
              <>
                <VSpacer size="4" />
                <TestUserBadge />
              </>
            )}
            <VSpacer size="4" />
            <Divider />
            <VSpacer size="5" />
            <Stack>
              <Text category="label-medium" sx={{ color: "#737373" }}>
                {t($ => $.customers.details.linkedAccountRole)}
              </Text>
              {!!customer.accountGroupRole && (
                <>
                  <VSpacer size="2" />
                  <CustomerRoleBadge
                    accountGroupRole={customer.accountGroupRole}
                  />
                </>
              )}
              <VSpacer size="5" />
            </Stack>
            {!!customer.accountGroup?.externalId && (
              <Stack>
                <Text category="label-medium" sx={{ color: "#737373" }}>
                  {t($ => $.customers.details.groupId)}
                </Text>
                <Text category="body-medium">{customer.accountGroup.externalId}</Text>
                <VSpacer size="5" />
              </Stack>
            )}
            <Stack>
              <Text category="label-medium" sx={{ color: "#737373" }}>
                {t($ => $.customers.details.phone)}
              </Text>
              <Text category="body-medium">{phone}</Text>
              <VSpacer size="5" />
            </Stack>
            <Stack>
              <Text category="label-medium" sx={{ color: "#737373" }}>
                {t($ => $.common.actions.email)}
              </Text>
              <Text
                category="body-medium"
                sx={{
                  overflowWrap: 'break-word',
                }}
              >
                {customer.email || '-'}
              </Text>
              <VSpacer size="5" />
            </Stack>
            <Stack>
              <Text category="label-medium" sx={{ color: "#737373" }}>
                {t($ => $.customers.details.address)}
              </Text>
              <Text category="body-medium" multiline>{address}</Text>
              <VSpacer size="5" />
            </Stack>
            <Stack>
              <Text category="label-medium" sx={{ color: "#737373" }}>
                {t($ => $.customers.details.erpId)}
              </Text>
              <Text category="body-medium">{customer.externalId || '-'}</Text>
            </Stack>
          </Card>
          {showMembershipCard && (
            <>
              <VSpacer size="5" />
              <ProgramMembershipCard
                customer={customer}
                energyCustomer={energyCustomer}
                isReadOnly={!isInternal}
              />
            </>
          )}
          {isAgronomyMember && !!rewardsProgram && (
            <>
              <VSpacer size="5" />
              <TierStatusCard
                canAddOverride={canAddOverride}
                farmerReward={farmerReward}
                onOpenModal={() => setShowOverrideModal(true)}
                rewardsProgram={rewardsProgram}
              />
            </>
          )}
          {!!energy && (
            <>
              <VSpacer size="5" />
              <EnergyStatusCard
                energy={energy}
                isReadOnly={isEnergyReadOnly}
                onAddOverride={() => setShowEnergyOverrideModal(true)}
                onManageBehaviors={() => (
                  setBehaviorsModalMode(isEnergyReadOnly ? 'view' : 'manage')
                )}
              />
            </>
          )}
        </Grid>
        <Grid item md={8} sm={12}>
          {(
            (isInternal || (isRetailerAdmin && retailerUser?.isChurnEnabled)) &&
              customer.churnStatus &&
              ![ChurnStatus.NoRisk, ChurnStatus.Insufficient].includes(customer.churnStatus)
          ) &&
            <>
              <ChurnBanner
                explanation={customer.churnExplanation}
                status={customer.churnStatus as ChurnStatus}
                testID="customer-churn-banner"
              />
              <VSpacer size="5" />
            </>
          }
          <Grid container spacing={3}>
            {isAgronomyMember && (
              <Grid item md={4} sm={12}>
                <CustomerDataCard
                  customer={customer}
                  label={energy
                    ? t($ => $.energy.profile.pointsAvailableAgronomy)
                    : t($ => $.customers.details.pointsAvailable)}
                  tooltipText={retailer?.hasBookings
                    ? t($ => $.shared.walletPointsAvailableTooltip.text)
                    : t($ => $.shared.walletPointsAvailableTooltip.textWithoutBookings)
                  }
                  value={isSecondaryAccount || isEnergyCustomerLoading ? null : (
                    formatNumber(agronomyPointsAvailable, i18next)
                  )}
                />
              </Grid>
            )}
            {!!energy && (
              <Grid item md={4} sm={12}>
                <CustomerDataCard
                  customer={customer}
                  label={t($ => $.energy.profile.pointsAvailableEnergy)}
                  value={formatNumber(energyPointsAvailable, i18next)}
                />
              </Grid>
            )}
            {isAgronomyMember && retailer?.hasBookings && (
              <Grid item md={4} sm={12}>
                <CustomerDataCard
                  customer={customer}
                  label={t($ => $.customers.details.pointsReserved)}
                  tooltipText={t($ => $.customers.details.pointsReservedTooltip)}
                  value={isSecondaryAccount ? null : (
                    formatNumber(totalPointsReserved, i18next)
                  )}
                />
              </Grid>
            )}
            {!!agronomyPointsLocked && (
              <Grid item md={4} sm={12}>
                <CustomerDataCard
                  customer={customer}
                  label={t($ => $.wallet.locked.title)}
                  tooltipText={t($ => $.wallet.locked.explanation)}
                  value={isSecondaryAccount ? null : (
                    formatNumber(agronomyPointsLocked, i18next)
                  )}
                />
              </Grid>
            )}
          </Grid>
          <VSpacer size="5" />
          {isSecondaryAccount && !!primaryCustomerId && (
            <>
              <Card testID={`${customer.id}-see-primary-card`}>
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Text category="body-large">
                    {t($ => $.customers.details.seePrimaryAccount)}
                  </Text>
                  <Button
                    onClick={() => {
                      navigate(Routes.CUSTOMER_DETAILS.replace(':id', primaryCustomerId));
                    }}
                    testID="navigate-to-primary-button"
                    variant="outlined"
                  >
                    {t($ => $.customers.details.view)}
                  </Button>
                </Stack>
              </Card>
              <VSpacer size="5" />
            </>
          )}
          <Card testID={`${customer.id}-rewards-details-card`}>
            {showRewardsProgramStatus && !!energy && (
              <>
                <EnergyProgramStatus
                  customer={customer}
                  energy={energy}
                  onViewProgress={() => setBehaviorsModalMode('view')}
                  retailer={retailer}
                />
                <VSpacer size="6" />
                <Divider />
                {isAgronomyMember && <VSpacer size="6" />}
              </>
            )}
            {showRewardsProgramStatus && isAgronomyMember && (
              <>
                <RewardsProgramStatus
                  customer={customer}
                  farmerRewardsProfiles={farmerRewards}
                  retailer={retailer}
                  rewardsProgram={rewardsProgram ?? undefined}
                />
                <VSpacer size="6" />
                <Divider />
              </>
            )}
            {!isSecondaryAccount && (
              <>
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  my="20px"
                >
                  <Text category="body-large">{t($ => $.customers.details.wallet)}</Text>
                  <Stack alignItems="center" direction="row">
                    {isInternal && (
                      <Button
                        onClick={() => setShowUpdatePointsModal(true)}
                        testID="update-points-button"
                        variant="text"
                      >
                        {t($ => $.customers.details.updatePoints)}
                      </Button>
                    )}
                    <HSpacer size="4" />
                    <Button
                      onClick={() => setShowWalletModal(true)}
                      testID="view-wallet-button"
                      variant="outlined"
                    >
                      {t($ => $.customers.details.view)}
                    </Button>
                  </Stack>
                </Stack>
                <Divider />
              </>
            )}
            <Stack alignItems="center" direction="row" justifyContent="space-between" my="20px">
              <Text category="body-large">{t($ => $.customers.details.pointsActivity)}</Text>
              <Button
                onClick={() => setShowPointsActivityModal(true)}
                testID="view-points-activity-button"
                variant="outlined"
              >
                {t($ => $.customers.details.view)}
              </Button>
            </Stack>
            <Divider />
            <Stack alignItems="center" direction="row" justifyContent="space-between" my="20px">
              <Text category="body-large">{t($ => $.customers.details.associatedSellers)}</Text>
              <Button
                onClick={() => setShowAssociatedSellersModal(true)}
                testID="view-associated-sellers-button"
                variant="outlined"
              >
                {t($ => $.customers.details.view)}
              </Button>
            </Stack>
            {isAgronomyMember && (
              <>
                <Divider />
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  marginTop="20px"
                >
                  <Text category="body-large">{t($ => $.customers.details.linkedAccounts)}</Text>
                  <Button
                    onClick={() => setShowLinkedAccountsModal(true)}
                    testID="view-linked-accounts-button"
                    variant="outlined"
                  >
                    {t($ => $.customers.details.view)}
                  </Button>
                </Stack>
              </>
            )}
          </Card>
        </Grid>
      </Grid>
      {showWalletModal && (
        <SubwalletListDialog
          balances={walletBalances}
          customer={customer}
          linkedAccounts={linkedAccounts}
          retailer={retailer}
          setShow={setShowWalletModal}
          show
        />
      )}
      {showUpdatePointsModal && (
        <UpdatePointsDialog farmer={customer} setShow={setShowUpdatePointsModal} show />
      )}
      {showEditCustomerModal && (
        <EditCustomerDialog
          customer={customer}
          onClose={() => setShowEditCustomerModal(false)}
        />
      )}
      {showOverrideModal && !!rewardsProgram && (
        <TierOverrideModal
          customer={customer}
          farmerReward={farmerReward}
          isJobProcessing={isJobProcessing}
          rewardsProgram={rewardsProgram}
          setShow={setShowOverrideModal}
          show
        />
      )}
      {showEnergyOverrideModal && !!energy && (
        <EnergyTierOverrideModal
          energy={energy}
          farmerId={customer.id}
          onClose={() => setShowEnergyOverrideModal(false)}
        />
      )}
      {!!behaviorsModalMode && !!energyCustomer?.energy && (
        <ManageBehaviorsModal
          customerName={accountName}
          energyCustomer={energyCustomer}
          onClose={() => setBehaviorsModalMode(null)}
          readOnly={behaviorsModalMode === 'view'}
        />
      )}
      {showPointsActivityModal && (
        <PointsActivityModal
          customerId={customerId}
          onClose={() => setShowPointsActivityModal(false)}
        />
      )}
      <AssociatedSellersDialog
        associatedSellers={associatedSellers ?? []}
        onClose={() => setShowAssociatedSellersModal(false)}
        show={showAssociatedSellersModal}
      />
      <LinkedAccountsDialog
        customer={customer}
        onClose={() => setShowLinkedAccountsModal(false)}
        onViewAccount={onViewAccount}
        show={isAgronomyMember && showLinkedAccountsModal}
      />
      {!!retailer && !!retailer.rewardsPrograms && (
        <ProgramPointsExpirationModal
          customer={customer}
          programs={agronomyProgramsOnly(retailer.rewardsPrograms)}
          retailer={retailer}
        />
      )}
    </Container>
  );
};

export default CustomerDetails;
